/**
 * Which reporting channel one row of hours was authored in — the place the
 * consultant (or the person acting for them) put the number on the record, not
 * the transport that carried it into the CRM.
 *
 * **This is the shared vocabulary with ERP.** The values are the string values
 * of ERP's `TimeSheetSource` (`@repo/enums`), verbatim, so a row can round-trip
 * CRM → ERP → CRM without a mapping table and `(source, sourceId)` means the
 * same pair on both sides. Adding a value is a change to both definitions in
 * the same breath, never to one of them.
 *
 * **Not the booking's source.** A work request sourced from Bemlo can have its
 * hours arrive on paper — that is the normal case, not an anomaly. Where the
 * demand came from is `BookingEntity.demandSource`, a separate field with its
 * own vocabulary.
 */
export enum TimeReportRowSource {
  /** Reported by the consultant in Bemlo; ERP reads it over the provider sync. */
  Bemlo = "Bemlo",
  /** Reported by the consultant in Medlo; a person transfers it onward today. */
  Medlo = "Medlo",
  /** Reported by the consultant in Inkopio; a person transfers it onward today. */
  Inkopio = "Inkopio",
  /** AI-parsed paper report, pushed into ERP by Agent Depot and exported here. */
  Paper = "Paper",
  /** Written by the consultant app. Stamped by the CRM on the app route, never sent by the app. */
  MobileApp = "MobileApp",
  /** Typed in the CRM by a person. Stamped by the CRM on the internal route, never sent by the caller. */
  Crm = "Crm",
  /** Entered directly in ERP by a user and exported here. */
  Manual = "Manual",
  /**
   * The channel is not known. Legitimate only on rows written before it was
   * recorded — see the note on retrofitting in ADR-0008. A writer choosing this
   * is a bug, not a fallback.
   */
  Unknown = "Unknown"
}

/**
 * The two channels the CRM itself is the author of.
 *
 * A row on one of these was stamped by the CRM from the route it arrived on, so
 * the value is a fact about where it was typed rather than a claim the caller
 * made — which is what lets ERP's import pull CRM-authored rows and ignore the
 * ones it exported itself, and why a caller cannot declare either (ADR-0008,
 * rule 3). Mirrors ERP's `CRM_AUTHORED_SOURCES`.
 */
export const CRM_AUTHORED_ROW_SOURCES: ReadonlySet<TimeReportRowSource> = new Set([
  TimeReportRowSource.MobileApp,
  TimeReportRowSource.Crm
]);

export function isCrmAuthoredRowSource(source: TimeReportRowSource): boolean {
  return CRM_AUTHORED_ROW_SOURCES.has(source);
}
